import React from 'react';
import {LoadingSpinner, Text, Toggle} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';
import {usePreviewLockImpactQuery} from './previewLockImpact.queries.generated';
import {GUIDE_LEVEL_LOCK_TYPE} from './manageRestrictionsModal.constants';

interface SchoolRestrictionRowProps {
  school: AdminSchool;
  guideLevelId?: string;
  isRestricted: boolean;
  onToggle: () => void;
}

// Builds the per-school impact line, omitting a zero clause; null when there's no impact at all.
const getImpactMessage = (closedAssignments: number, activeAssignments: number): string | null => {
  const clauses: string[] = [];
  if (closedAssignments > 0) {
    clauses.push(`${closedAssignments} closed ${closedAssignments === 1 ? 'assignment' : 'assignments'}`);
  }
  if (activeAssignments > 0) {
    clauses.push(`${activeAssignments} active ${activeAssignments === 1 ? 'assignment' : 'assignments'}`);
  }
  if (clauses.length === 0) {
    return null;
  }
  return `Questions will be blocked in ${clauses.join(' and ')}.`;
};

export const SchoolRestrictionRow = ({
  school,
  guideLevelId,
  isRestricted,
  onToggle
}: SchoolRestrictionRowProps) => {
  const {data, loading} = usePreviewLockImpactQuery({
    variables: {lockType: GUIDE_LEVEL_LOCK_TYPE, sourceId: guideLevelId ?? '', schoolId: school.id},
    skip: !guideLevelId
  });
  const impact = data?.previewLockImpact;
  // Shown regardless of restrict state (per the Figma annotation).
  const impactMessage = impact
    ? getImpactMessage(impact.closedAssignmentCount, impact.openAssignmentCount)
    : null;

  return (
    <tr className='border-b border-border-subtle last:border-b-0'>
      <td className='w-full px-x2 py-x1-half'>
        <Text as='div'>{school.name}</Text>
        {/* Reserve the impact line with a spinner while in flight so the row height doesn't jump. */}
        <div className='mt-x0-quarter' aria-busy={loading}>
          {loading ? (
            <LoadingSpinner
              size='lg'
              bottomMarginLevel={0}
              className='w-fit'
              assistiveText='Loading assignment impact'
            />
          ) : (
            impactMessage && (
              <Text as='div' size='s' color='tertiary'>
                {impactMessage}
              </Text>
            )
          )}
        </div>
      </td>
      <td className='whitespace-nowrap border-l border-border-primary px-x2 py-x1-half text-left align-middle'>
        <Toggle checked={isRestricted} onChange={onToggle} aria-label={`Restrict ${school.name}`} />
      </td>
    </tr>
  );
};

export default SchoolRestrictionRow;
